import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminDashboard from '../components/Admin/AdminDashboard.js';
import AdminUsers from '../components/Admin/AdminUsers.js';
import AdminContent from '../components/Admin/AdminContent.js';
import AdminAnalytics from '../components/Admin/AdminAnalytics.js';
import AdminSettings from '../components/Admin/AdminSettings.js';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function AdminPage() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isLoaded) {
      const fallbackStatus = user?.publicMetadata?.isAdmin === true;
      getToken().then(async (token) => {
        const response = await fetch('/api/admin?view=me', { headers: { Authorization: `Bearer ${token}` } });
        const adminStatus = response.ok;
        setIsAdmin(adminStatus);
        setChecking(false);
        if (!adminStatus) setTimeout(() => navigate('/'), 2000);
      }).catch(() => {
        setIsAdmin(fallbackStatus);
        setChecking(false);
        if (!fallbackStatus) setTimeout(() => navigate('/'), 2000);
      });
    }
  }, [isLoaded, user, navigate, getToken]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!isLoaded || checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Access Denied. You do not have admin privileges. Redirecting...
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Admin Dashboard
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '0.95rem',
            },
          }}
        >
          <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Users" />
          <Tab icon={<LibraryBooksIcon />} iconPosition="start" label="Content" />
          <Tab icon={<AnalyticsIcon />} iconPosition="start" label="Analytics" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <AdminDashboard />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <AdminUsers />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <AdminContent />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <AdminAnalytics />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <AdminSettings />
      </TabPanel>
    </Box>
  );
}

export default AdminPage;
